'use client';

import { useMemo, useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

export type OneRepMaxVariant = 'one-rep-max-calculator' | 'bench-press-calculator';

// Standard published %1RM-to-reps table used across strength coaching (Epley-consistent).
const PERCENT_TABLE = [100, 95, 90, 85, 80, 75, 70, 65, 60];
const REPS_AT_PERCENT: Record<number, number> = {
  100: 1,
  95: 2,
  90: 4,
  85: 6,
  80: 8,
  75: 10,
  70: 12,
  65: 15,
  60: 20,
};

export default function BrowserOneRepMaxCalculator({ variant }: { variant: OneRepMaxVariant }) {
  const fired = useRef(false);
  const fireUsage = () => {
    if (fired.current) return;
    fired.current = true;
    void consumeToolUsage(variant, '').catch(() => undefined);
  };

  const isBench = variant === 'bench-press-calculator';
  const [weight, setWeight] = useState(isBench ? 185 : 135);
  const [reps, setReps] = useState(5);
  const [unit, setUnit] = useState<'lb' | 'kg'>('lb');

  function onChange<T>(setter: (v: T) => void) {
    return (v: T) => {
      fireUsage();
      setter(v);
    };
  }

  const result = useMemo(() => {
    const r = Math.max(1, Math.min(20, reps));
    // Epley formula: the standard, most widely published 1RM estimate.
    const oneRepMax = r === 1 ? weight : weight * (1 + r / 30);
    const table = PERCENT_TABLE.map((pct) => ({
      pct,
      weight: (oneRepMax * pct) / 100,
      reps: REPS_AT_PERCENT[pct],
    }));
    return { oneRepMax, table };
  }, [weight, reps]);

  const lift = isBench ? 'bench press' : 'lift';

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Fitness</span>
        <h2 className="tool-live-title">
          {isBench ? 'Bench press calculator' : 'One rep max calculator'}
        </h2>
        <p className="tool-live-subtitle">
          Enter a weight and the reps you got with it to estimate your one-rep max for the {lift},
          plus a training percentage chart.
        </p>
      </div>

      <div className="tool-live-grid">
        <section
          className="tool-live-uploader"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}
        >
          <label className="tool-live-field">
            <span>Weight lifted</span>
            <input
              min={0}
              onChange={(e) => onChange(setWeight)(Number(e.target.value) || 0)}
              type="number"
              value={weight}
            />
          </label>
          <label className="tool-live-field">
            <span>Unit</span>
            <select onChange={(e) => onChange(setUnit)(e.target.value as 'lb' | 'kg')} value={unit}>
              <option value="lb">lb</option>
              <option value="kg">kg</option>
            </select>
          </label>
          <label className="tool-live-field">
            <span>Reps completed</span>
            <input
              max={20}
              min={1}
              onChange={(e) => onChange(setReps)(Number(e.target.value) || 1)}
              type="number"
              value={reps}
            />
          </label>
        </section>

        <section className="tool-live-preview">
          <p className="tool-live-panel-note">
            <strong>
              {result.oneRepMax.toFixed(1)} {unit}
            </strong>
            <span>Estimated one-rep max ({lift})</span>
          </p>
          {result.table.map((row) => (
            <p
              className="tool-live-panel-note"
              key={row.pct}
              style={{ padding: '0.45rem 0.75rem' }}
            >
              <span>
                {row.pct}% ={' '}
                <strong>
                  {row.weight.toFixed(1)} {unit}
                </strong>{' '}
                (~{row.reps} rep{row.reps > 1 ? 's' : ''})
              </span>
            </p>
          ))}
          <p className="tool-live-panel-note">
            Uses the Epley formula, most accurate for sets of 10 reps or fewer. Treat this as a
            training estimate, not a number to attempt cold in the gym.
          </p>
        </section>
      </div>
    </div>
  );
}
